// ─────────────────────────────────────────────────────────────────────────────
// installDemoAdapter.js — routes matching requests to the demo dataset.
//
// HOW IT HOOKS IN
// An axios request interceptor swaps out `config.adapter` for the demo one when
// (a) demo mode is on and (b) demoApi has a handler for the URL. Replacing the
// adapter rather than throwing means the request never reaches the network at
// all, and every caller still gets a normal axios response — no page needs to
// know it is being served fixtures.
//
// WHY INTERCEPT AT ALL RATHER THAN LET THE SERVER ANSWER
// The demo account already has server-side scrubbing (middleware/demoAnonymizer)
// which renames clients and blanks photo URLs. Short-circuiting here means the
// four demo pages show the curated dataset with real client names and real
// photos, and never see the scrubber. It also guarantees a demo session can
// never write to production: every mutating call is swallowed (see demoApi's
// WRITE_ROUTES) instead of being forwarded.
// ─────────────────────────────────────────────────────────────────────────────

import { handleDemoRequest } from './demoApi';
import { initDemoStore } from './demoStore';

const FORCE_KEY = 'nova_demo_force';
export const TOAST_EVENT = 'nova-demo-toast';
export const DEMO_CHANGE_EVENT = 'nova-demo-mode-change';

/**
 * Network requests resolve instantly from memory, which is actually a problem:
 * every skeleton and spinner is skipped, and the result reads as a static mock
 * rather than a live app. A short delay restores the app's normal rhythm.
 */
const MIN_LATENCY_MS = 90;
const MAX_LATENCY_MS = 240;

/** True when the signed-in account is the demo account. */
export function isDemoUser() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const user = JSON.parse(raw);
    return user && user.is_demo === true;
  } catch (_) {
    return false;
  }
}

/**
 * Manual override, for testing the demo from a normal login.
 * `?demo=1` turns it on, `?demo=0` turns it off, and the choice sticks.
 */
export function isForced() {
  try {
    return localStorage.getItem(FORCE_KEY) === '1';
  } catch (_) {
    return false;
  }
}

export function setForced(on) {
  try {
    if (on) localStorage.setItem(FORCE_KEY, '1');
    else localStorage.removeItem(FORCE_KEY);
  } catch (_) { /* storage disabled — nothing to persist */ }
  window.dispatchEvent(new CustomEvent(DEMO_CHANGE_EVENT));
}

/** Demo mode is on for the demo account, or whenever it has been forced on. */
export function isDemoMode() {
  return isForced() || isDemoUser();
}

/** Read ?demo= once at startup so a link can switch the mode. */
export function readDemoFlagFromUrl() {
  try {
    const flag = new URLSearchParams(window.location.search).get('demo');
    if (flag === '1') setForced(true);
    else if (flag === '0') setForced(false);
  } catch (_) { /* malformed query string — ignore */ }
}

const jitter = () => MIN_LATENCY_MS + Math.random() * (MAX_LATENCY_MS - MIN_LATENCY_MS);

/** Tell the badge to show a message. No-op if the badge is not mounted. */
export function demoToast(message, tone = 'info') {
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail: { message, tone } }));
}

function makeResponse(config, result) {
  return {
    data: result.data,
    status: result.status,
    statusText: result.status === 200 ? 'OK' : 'Error',
    headers: { 'x-nova-demo': '1' },
    config,
    request: { demo: true, path: result.demoPath },
  };
}

/**
 * Custom axios adapter. Resolves 2xx, rejects 4xx/5xx the way axios itself would
 * so `catch` blocks in the pages behave normally.
 */
function demoAdapter(result) {
  return (config) => new Promise((resolve, reject) => {
    setTimeout(() => {
      const response = makeResponse(config, result);

      if (result.demoReadOnly) {
        demoToast('Read-only demo — that change was not saved.', 'warn');
      }

      if (result.status >= 200 && result.status < 300) {
        resolve(response);
        return;
      }

      const err = new Error(`Request failed with status code ${result.status}`);
      err.config = config;
      err.response = response;
      err.isAxiosError = true;
      reject(err);
    }, jitter());
  });
}

let installed = false;

/**
 * Install the interceptor. Safe to call once at startup regardless of who is
 * logged in — it checks isDemoMode() per request, so signing in or out of the
 * demo account takes effect immediately without a reload.
 */
export function installDemoAdapter(axios, apiBase) {
  if (installed) return;
  installed = true;

  readDemoFlagFromUrl();

  // Hydrating stored photos is async and the pages render before it finishes.
  // getData() works off the seed regardless, so there is nothing to await here;
  // uploaded photos simply appear once IDB has been read.
  initDemoStore().catch(() => { /* reported inside initDemoStore */ });

  axios.interceptors.request.use((config) => {
    if (!isDemoMode()) return config;

    // Auth must always hit the real server, or nobody could log in or out.
    const url = String(config.url || '');
    if (url.includes('/auth/')) return config;

    const result = handleDemoRequest({
      method: config.method,
      url,
      baseUrl: config.baseURL || apiBase,
      params: config.params,
    });

    if (!result) return config; // no handler — let it go to the network

    return { ...config, adapter: demoAdapter(result) };
  });
}

export default installDemoAdapter;
